import { sql } from "drizzle-orm";
import { useEffect, useState } from "react";
import { CharacterList } from "~/components/metrika/CharacterList";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { $LOADER, $PAGE, callApi } from "~/util/Loaders";
import { metrikaApi } from "../api/metrika";
import { GameVer, tturl, useRealm } from "~/util/Lang";
import { Link } from "react-router";

const botPatterns = [
  "bot",
  "Bot",
  "Spider",
  "spider",
  "Crawler",
  "crawler",
  "ImagesiftBot",
  "YandexBot",
  "Yatcybot",
  "MJ12bot",
  "bingbot",
  "Googlebot",
  "Amazonbot",
  "ChatGPT",
  "Twitterbot",
  "Baiduspider",
  "Google-Read-Aloud",
  "https://",
];

function isBot(userAgent: string): boolean {
  return botPatterns.some((pattern) => userAgent.includes(pattern));
}

$PAGE({
  path: "metrika",
  loader: $LOADER?.(async (args) => {
    return { authUser: args.req.sess?.user };
  }),
  render: ({ data }) => {
    const realm = useRealm();
    const [events, setEvents] = useState<
      {
        id: number;
        userAgent: string | null;
        referer: string | null;
        path: string | null;
        ip: string | null;
        gver: string | null;
        lang: string | null;
        createdAt: Date;
      }[]
    >([]);
    const [newEventIds, setNewEventIds] = useState<Set<number>>(new Set());
    const [knownIps, setKnownIps] = useState<Set<string>>(new Set());
    const [uniqueUsersCount, setUniqueUsersCount] = useState(0);
    const [topIps, setTopIps] = useState<Array<{ ip: string; count: number }>>(
      []
    );

    useEffect(() => {
      const fetchEvents = async () => {
        try {
          const res = await callApi(metrikaApi, { input: {}, realm });
          const result = res.data;

          const filtered = result.events.filter(
            (e: any) => !isBot(e.userAgent as string)
          );

          // Считаем количество посещений по каждому IP
          const ipCountMap = new Map<string, number>();
          for (const event of filtered) {
            if (event.ip) {
              ipCountMap.set(event.ip, (ipCountMap.get(event.ip) || 0) + 1);
            }
          }

          // Преобразуем в массив и сортируем по убыванию
          const ipList = Array.from(ipCountMap.entries()).map(
            ([ip, count]) => ({
              ip,
              count,
            })
          );
          ipList.sort((a, b) => b.count - a.count);

          // Берём топ-10
          setTopIps(ipList.slice(0, 10));

          const existingIds = new Set(events.map((e) => e.id));
          const addedEvents = filtered.filter(
            (e: any) => !existingIds.has(e.id)
          );

          if (addedEvents.length > 0) {
            const newIdSet = new Set([
              ...newEventIds,
              ...addedEvents.map((e: any) => e.id),
            ]);
            setNewEventIds(newIdSet);
            setEvents(filtered);

            // Фильтруем только новые IP
            const newIps = addedEvents
              .map((e) => e.ip)
              .filter((ip): ip is string => !!ip && !knownIps.has(ip));

            if (newIps.length > 0) {
              newIps.forEach((ip) => knownIps.add(ip));
              setUniqueUsersCount(knownIps.size);
            }
          } else {
            setEvents(filtered);
          }
        } catch (error) {
          console.error("Ошибка загрузки событий:", error);
        }
      };

      // Первый раз сразу
      fetchEvents();

      // Каждые 5 секунды обновляем
      const interval = setInterval(fetchEvents, 5000);

      // Убираем подсветку новых записей через 3 сек
      const highlightTimeout = setInterval(() => {
        setNewEventIds((prev) => {
          const updated = new Set(prev);
          updated.forEach((id) => {
            setTimeout(() => updated.delete(id), 1500); // убираем подсветку через 1.5 сек
          });
          return new Set(updated);
        });
      }, 1000);
      return () => {
        clearInterval(interval);
        clearInterval(highlightTimeout);
      };
    }, [events]);

    return (
      <div className="content">
        <PageTitle>Метрика - хуетрика</PageTitle>
        <div style={{ width: "50%", marginBlock: 10 }}>
          <strong>Уникальные пользователи:</strong> {uniqueUsersCount}
          <br />
          <strong>Всего событий:</strong> {events.length}
        </div>

        {topIps.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: "40px" }}>
            <h3 style={{ marginBottom: "10px" }}>
              Топ {topIps.length} активных IP
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #ccc" }}>
                  <th style={{ padding: "10px" }}>IP-адрес</th>
                  <th style={{ padding: "10px" }}>Просмотров</th>
                </tr>
              </thead>
              <tbody>
                {topIps.map(({ ip, count }, index) => (
                  <tr
                    key={ip}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                    }}
                  >
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      {ip}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      {count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Таблица */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              textAlign: "left",
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: "2px solid #ccc",
                }}
              >
                <th style={{ padding: "10px" }}>Время</th>
                <th style={{ padding: "10px" }}>IP-адрес</th>
                <th style={{ padding: "10px" }}>User-Agent</th>
                <th style={{ padding: "10px" }}>Путь</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event, index) => {
                const date = new Date(event.createdAt);
                const timeString = `${date
                  .getHours()
                  .toString()
                  .padStart(2, "0")}:${date
                  .getMinutes()
                  .toString()
                  .padStart(2, "0")}:${date
                  .getSeconds()
                  .toString()
                  .padStart(2, "0")}`;

                const pathElement = (
                  <Link
                    to={tturl(
                      { game: event.gver as GameVer, lang: "en" },
                      (event.path as string) ?? "/"
                    )}
                  >
                    {event.path}
                  </Link>
                );

                return (
                  <tr
                    key={event.id}
                    style={{
                      transition: "background-color 1s ease",
                    }}
                  >
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      {timeString}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      {event.ip || "-"}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      <small>{event.userAgent}</small>
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>
                      {event.referer ? (
                        <>
                          {event.gver} {event.referer} → {pathElement}
                        </>
                      ) : (
                        pathElement
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
});
